import { Injectable } from '@angular/core';
import type { MapboxMap } from '@polaris-customerweb/sharedcomponents.core';
import { Dealer } from '@polaris-customerweb/sharedcomponents.map';
import { ConsoleLogService } from '@polaris-customerweb/sharedcomponents.shared';
import { compact, uniq } from 'lodash-es';
import { FilterSpecification, PointLike } from 'mapbox-gl';
import { DealerService } from 'src/app/services/dealer.service';
import { MapContentMapService } from 'src/app/services/map-content-map.service';
import { MapService } from 'src/app/services/map.service';

@Injectable({ providedIn: 'root' })
export class DealerMapService extends MapContentMapService {
    private selectedIds: string[] = [];
    private hiddenIds: string[];
    constructor(
        consoleLogService: ConsoleLogService,
        private dealerService: DealerService,
        mapService: MapService
    ) {
        super(consoleLogService, mapService);
        super.setSourceId('dealers');
    }

    override addSources(mapboxMap: MapboxMap): void {
        const layerIds = this.mapService.getLayerIds(mapboxMap);
        this.getLayerIds(layerIds);
        this._layerIdsSelectable = [
            ...this.layerIdsUnselected,
            ...this.layerIdsSelected,
        ];
    }

    override setMultiSelectFilter(
        mapboxMap: MapboxMap,
        selectedIds: string | string[]
    ): void {
        this.selectedIds = Array.isArray(selectedIds)
            ? selectedIds
            : [selectedIds];
        super.setMultiSelectFilter(mapboxMap, selectedIds, true);
        this.setHiddenDealers(mapboxMap, this.hiddenIds);
    }

    override setSelectedFilter(
        mapboxMap: MapboxMap,
        selectedIds?: string | number | string[] | number[]
    ): void {
        this.selectedIds = Array.isArray(selectedIds)
            ? selectedIds.map((id: string | number) => String(id))
            : [String(selectedIds)];
        super.setSelectedFilter(mapboxMap, selectedIds, true);
        this.setHiddenDealers(mapboxMap, this.hiddenIds);
    }

    getDealerAtPoint(
        mapboxMap: MapboxMap,
        point: PointLike
    ): Dealer | undefined {
        const features = this.mapService.getFeaturesAtPoint(
            mapboxMap,
            point,
            this.layerIds
        );
        const dealer = features[0];

        if (!dealer) {
            return undefined;
        }

        return this.dealerService.getDealerByIdFromCache(
            dealer.properties?.id as string
        );
    }

    setHiddenDealers(mapboxMap: MapboxMap, ids: string[]) {
        this.hiddenIds = ids;
        this.layerIdsUnselected.forEach((id) => {
            let filter = mapboxMap.getFilter(id) as FilterSpecification;
            const list = compact(uniq([...ids, ...this.selectedIds]));
            filter = this.updateFilterWithSelectedIds(
                filter,
                list.length ? list : ['SELECTED_ID']
            );
            this.mapService.setFilter(mapboxMap, id, filter);
        });
    }
}
